/**
 * 관리자 결제 대시보드
 * - 전체 통계 조회
 * - 오너별 결제 상태 조회
 * - 플랜별 통계
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config/api';

interface PaymentStats {
  total_owners: number;
  paid_owners: number;
  pending_owners: number;
  total_monthly_revenue: number;
  plan_distribution: Record<string, number>;
}

interface OwnerPayment {
  owner_id: string;
  owner_name: string;
  owner_email: string;
  organization_name: string;
  plan_name: string;
  plan_price: number;
  status: string;
  payment_status: string;
  end_date: string;
  student_count: number;
}

interface PlanStat {
  plan_id: string;
  plan_name: string;
  plan_price: number;
  subscriber_count: number;
  monthly_revenue: number;
  avg_students_per_org: number;
}

const AdminPaymentDashboard: React.FC = () => {
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [owners, setOwners] = useState<OwnerPayment[]>([]);
  const [planStats, setPlanStats] = useState<PlanStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchDashboardData();
  }, [currentPage]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // 전체 통계
      const statsRes = await fetch(`${API_BASE_URL}/api/v1/admin/payments/dashboard`, { headers });
      const statsData = await statsRes.json();
      setStats(statsData);

      // 오너별 결제 상태
      const ownersRes = await fetch(`${API_BASE_URL}/api/v1/admin/payments/owners?page=${currentPage}&page_size=10`, { headers });
      const ownersData = await ownersRes.json();
      setOwners(ownersData.owners);
      setTotalPages(Math.ceil(ownersData.total / 10));

      // 플랜별 통계
      const planRes = await fetch(`${API_BASE_URL}/api/v1/admin/payments/plans/statistics`, { headers });
      const planData = await planRes.json();
      setPlanStats(planData);
    } catch (error) {
      console.error('결제 데이터 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'active': 'bg-green-100 text-green-800',
      'expired': 'bg-red-100 text-red-800',
      'cancelled': 'bg-gray-100 text-gray-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      'paid': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">결제 관리 대시보드</h1>

      {/* 전체 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">전체 오너</h3>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_owners}명</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">결제 완료</h3>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats.paid_owners}명</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">미결제</h3>
            <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending_owners}명</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">월 총 수익</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">
              {formatCurrency(stats.total_monthly_revenue)}
            </p>
          </div>
        </div>
      )}

      {/* 플랜별 통계 */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">플랜별 통계</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">플랜</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">가격</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">가입자</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">월 매출</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">평균 학생 수</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {planStats.map((plan) => (
                <tr key={plan.plan_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {plan.plan_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(plan.plan_price)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {plan.subscriber_count}명
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(plan.monthly_revenue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {plan.avg_students_per_org.toFixed(1)}명
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 오너별 결제 상태 */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">오너별 결제 상태</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">오너</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">조직</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">플랜</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">구독 상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">결제 상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">만료일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">학생 수</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {owners.map((owner) => (
                <tr key={owner.owner_id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{owner.owner_name}</div>
                    <div className="text-sm text-gray-500">{owner.owner_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {owner.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{owner.plan_name}</div>
                    <div className="text-sm text-gray-500">{formatCurrency(owner.plan_price)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(owner.status)}`}>
                      {owner.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusBadge(owner.payment_status)}`}>
                      {owner.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {owner.end_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {owner.student_count}명
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            이전
          </button>
          <span className="text-sm text-gray-700">
            페이지 {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            다음
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentDashboard;
